// frontend/src/components/Feedback.js
import { useEffect, useState } from "react";
import { createMessage, listMessages, ApiError } from "../lib/api";

// Test messages to hide on the front end. Add entries here to hide more.
// Matching is case-insensitive and ignores surrounding whitespace.
const HIDDEN_MESSAGES = new Set([
  "fjvjghb",
  "hkhk",
  "hj",
]);

function isHidden(message) {
  const content = (message.content || "").trim().toLowerCase();
  return HIDDEN_MESSAGES.has(content);
}

export default function Feedback() {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");

  async function load() {
    try {
      const data = await listMessages(50);
      const all = Array.isArray(data) ? data : [];
      setMessages(all.filter((m) => !isHidden(m)));
    } catch {
      /* feedback list is optional */
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function send(e) {
    e.preventDefault();
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    setError("");
    try {
      await createMessage(content);
      setText("");
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(
          err.retryAfter
            ? `Too many messages. Try again in ${err.retryAfter}s.`
            : "Too many messages. Please slow down."
        );
      } else {
        setError("Could not send. Please try again.");
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="mx-auto mt-12 max-w-3xl">
      <h2 className="text-sm font-semibold text-gray-900">Feedback</h2>
      <p className="mt-1 text-xs text-gray-500">
        Tell us what worked, what confused you, or what you'd like CyberCheck to check next.
      </p>

      <form onSubmit={send} className="mt-3 flex flex-col gap-2 sm:flex-row">
        <label className="sr-only" htmlFor="feedback-input">
          Your feedback
        </label>
        <input
          id="feedback-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Leave a short message…"
          maxLength={2000}
          disabled={sending}
          className="flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-gray-900"
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          className="rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-black disabled:opacity-40"
        >
          {sending ? "…" : "Send"}
        </button>
      </form>

      {error && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}

      {messages.length > 0 && (
        <ul className="mt-4 space-y-2">
          {messages.slice(0, 5).map((m) => (
            <li
              key={m.id}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
            >
              <p className="whitespace-pre-wrap break-words text-gray-800">{m.content}</p>
              <p className="mt-1 text-[11px] text-gray-400">
                {new Date(m.created_at).toLocaleString()}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}